import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { TokenSearchBar } from './mobile/TokenSearchBar';
import { TokenWithMetadata } from '@/hooks/explore/useTrendingTokens';
import { TokenResult } from '@/hooks/explore/useTokenSearch';
import classNames from '@/lib/misc/classNames';
import MobileMenu from '../shared/MobileMenu';
import { useSearchHome } from '@/contexts/SearchHomeProvider';
import useAuthGuard from '@/hooks/auth/useAuthGuard';
import { useSession } from '@/contexts/LitSessionProvider';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import NetworkTimeSelector from './mobile/NetworkTimeSelector';
import { useMobileHomeList } from '@/hooks/explore/useMobileHomeList';
import HomeRow from './mobile/HomeRow';
import { useRecentToken } from '@/hooks/saved/useRecentToken';
import { useMixpanel } from '@/lib/misc/mixpanel';
import AssetDetailsModal from './AssetDetailsModal';
import CashModal from './CashModal';
import MobileDeposit from '../deposit/MobileDeposit';
import useAppSettings, { AppConfigKeys } from '@/hooks/admin/useAppSettings';
import useAppDetails from '@/hooks/utilities/useAppDetails';
export type SearchPageProps = {
  setSelectToAsset?: (_bool: boolean) => void;
  onTokenClick?: (token: TokenResult | TokenWithMetadata) => void;
  className?: string;
};
export default function SearchHome({
  setSelectToAsset,
  onTokenClick,
  className
}: SearchPageProps) {
  const router = useRouter();
  const {
    PKP
  } = useSession();
  const {
    isUserWithFunds,
    shouldOnboard,
    showReferralInput,
    hasUsername
  } = useAuthGuard();
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [assetModalData, setAssetModalData] = useState<{
    change: number;
    price: number;
  } | null>(null);
  const [cashModalData, setCashModalData] = useState<any | null>(null);
  const urlHasTrade = router.asPath?.includes('trade');
  const {
    handleSaveRecent
  } = useRecentToken();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const listRef = useRef<List>(null);
  useEffect(() => {
    urlHasTrade && setMode('memes');
  }, []);
  const {
    data: assetData,
    isLoading: isLoadingAssetData
  } = useERC20Balance({
    evmAddresses: [PKP?.additionalEthAddress],
    solanaAddresses: [PKP?.solanaAddress],
    useGeniusAPI: true
  });
  const {
    keyword,
    setKeyword,
    network,
    timeFrame,
    subMode,
    setSubMode,
    subMemeData,
    resultsToUse,
    handleSearch,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    mode,
    setMode,
    allTokens,
    loadingModes,
    recentItems,
    showWildWest,
    wildWest,
    setNetwork,
    defaultNetwork,
    setTimeFrame,
    handleClearSavedTokens
  } = useSearchHome();
  function handleTokenClick(tokenItem: any, searchResult: any) {
    if (tokenItem?.symbol === 'ETH' && mode === 'native') {
      setIsDepositModalOpen(true);
      return;
    }
    if (isDepositModalOpen) {
      return;
    }
    handleSaveRecent({
      tokenData: {
        ...searchResult,
        ...tokenItem,
        address: tokenItem?.address,
        networkId: tokenItem?.networkId,
        imageLargeUrl: tokenItem?.imageLargeUrl,
        symbol: tokenItem?.symbol,
        token: tokenItem,
        timeStamp: Date.now()
      }
    });
    if (onTokenClick) {
      LogToMixpanel({
        pkp: PKP,
        eventName: EVENTS.search.clickToken,
        category: CATEGORIES.search,
        metaData: {
          tokenAddress: tokenItem?.address,
          network: tokenItem.networkId
        }
      });
      onTokenClick(tokenItem);
    } else {
      router.push(`/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}`);
    }
  }
  const {
    setMobileMode
  } = useAppDetails();
  const virtualizedItems = useMobileHomeList({
    mode,
    resultsToUse,
    recentItems,
    timeFrame,
    shouldOnboard,
    showReferralInput,
    hasUsername,
    keyword,
    loadingModes,
    useUnprotectedSearch,
    allTokens,
    subMemeData,
    network,
    urlHasTrade,
    isUserWithFunds,
    isLoadingAssetData,
    assetData,
    handleClearSavedTokens
  });
  const itemData = useMemo(() => ({
    items: virtualizedItems,
    mode,
    wildWest,
    showWildWest,
    timeFrame,
    onTokenClick,
    recentItems,
    isUserWithFunds,
    isLoadingAssetData,
    assetData,
    keyword,
    urlHasTrade,
    setIsDepositModalOpen,
    setIsAssetModalOpen,
    setAssetModalData,
    setMode,
    setKeyword,
    subMode,
    setSubMode,
    handleTokenClick,
    loadingModes,
    useUnprotectedSearch,
    setUseUnprotectedSearch,
    handleSaveRecent,
    setCashModalData,
    setMobileMode
  }), [virtualizedItems, mode, wildWest, timeFrame, recentItems, isUserWithFunds, isLoadingAssetData, assetData, keyword, urlHasTrade, subMode, loadingModes, useUnprotectedSearch, setMobileMode]);
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [virtualizedItems, mode, wildWest, timeFrame, recentItems, isUserWithFunds, isLoadingAssetData, assetData, keyword, urlHasTrade, subMode, loadingModes, useUnprotectedSearch]);
  const getItemSize = useCallback((index: number) => {
    return virtualizedItems[index].height;
  }, [virtualizedItems]);
  const {
    appConfig
  } = useAppSettings();
  return <div className={classNames('bg-genius-indigo no-scrollbar h-screen overflow-hidden', className)} data-sentry-component="SearchHome" data-sentry-source-file="SearchHome.tsx">
      <div className='w-full'>
        <TokenSearchBar onSearch={handleSearch} setSelectToAsset={onTokenClick ? setSelectToAsset : undefined} setMode={setMode} mode={mode} startFocused={router.query.mode === 'search'} data-sentry-element="TokenSearchBar" data-sentry-source-file="SearchHome.tsx" />
      </div>

      {appConfig?.[AppConfigKeys.alertMessage] && <div className='px-4 text-[12px] my-2  text-genius-cream p-4 border-red-400 border-[1px] rounded-[20px] bg-[#F8717126] mx-4'>
          {appConfig?.[AppConfigKeys.alertMessage]}
        </div>}

      <AutoSizer data-sentry-element="AutoSizer" data-sentry-source-file="SearchHome.tsx">
        {({
        height,
        width
      }) => <List ref={listRef} key={`${mode}-${timeFrame}`} height={height} width={width} itemCount={virtualizedItems.length} itemSize={getItemSize} itemData={itemData} className='no-scrollbar' style={{
        paddingBottom: '200px'
      }}>
            {HomeRow}
          </List>}
      </AutoSizer>

      <div className={`flex
          w-full 
          gap-1.5 items-center           
          border-genius-blue
          overflow-x-auto no-scrollbar pl-2`}>
        <NetworkTimeSelector network={network} defaultNetwork={defaultNetwork} setNetwork={setNetwork} timeFrame={timeFrame} setTimeFrame={setTimeFrame} data-sentry-element="NetworkTimeSelector" data-sentry-source-file="SearchHome.tsx" />
      </div>

      <MobileMenu hasShadow={true} data-sentry-element="MobileMenu" data-sentry-source-file="SearchHome.tsx" />

      {/* Modals */}
      <MobileDeposit isModalOpen={isDepositModalOpen} setIsModalOpen={setIsDepositModalOpen} data-sentry-element="MobileDeposit" data-sentry-source-file="SearchHome.tsx" />
      <AssetDetailsModal onTokenClick={onTokenClick} isModalOpen={isAssetModalOpen} toggleModal={() => setIsAssetModalOpen(!isAssetModalOpen)} change={assetModalData?.change} price={assetModalData?.price} data-sentry-element="AssetDetailsModal" data-sentry-source-file="SearchHome.tsx" />
      <CashModal isModalOpen={cashModalData !== null} toggleModal={() => setCashModalData(null)} token={cashModalData} onTokenClick={onTokenClick} data-sentry-element="CashModal" data-sentry-source-file="SearchHome.tsx" />
    </div>;
}